/**
 * Cursor pagination controls.
 *
 * Cursors, not page numbers, because the API pages by cursor — and it does so
 * for a reason worth repeating here: with `OFFSET`, rows arriving at the top
 * while someone reads page two silently push records across the boundary, so
 * they see one twice and never see another. An audit log is exactly where that
 * matters.
 *
 * The consequence is that there is no "page 7" to jump to, and pretending
 * otherwise in the interface would be a lie about what the data can do.
 */

import "./data.css";

export interface PaginationProps {
  onPrevious?: (() => void) | undefined;
  onNext?: (() => void) | undefined;
  /** Translated by the caller. */
  previousLabel: string;
  nextLabel: string;
  /** e.g. "Showing 25 of 240". Announced politely when it changes. */
  summary?: string;
  busy?: boolean;
}

export function Pagination({
  onPrevious,
  onNext,
  previousLabel,
  nextLabel,
  summary,
  busy = false,
}: PaginationProps) {
  return (
    <nav className="hardauto-pagination" aria-label={summary ?? nextLabel}>
      <button
        type="button"
        className="hardauto-button"
        data-variant="secondary"
        data-size="sm"
        onClick={onPrevious}
        disabled={!onPrevious || busy}
      >
        {previousLabel}
      </button>

      {summary ? (
        <p className="hardauto-pagination__summary" aria-live="polite">
          {summary}
        </p>
      ) : null}

      <button
        type="button"
        className="hardauto-button"
        data-variant="secondary"
        data-size="sm"
        onClick={onNext}
        disabled={!onNext || busy}
      >
        {nextLabel}
      </button>
    </nav>
  );
}
